/**
 * Represent the Marketing page for the CMS model
 *
 * @module views/components/FullBleedHero
 * @memberof -Common
 */

import './FullBleedHero.scss';

import React, { useRef } from 'react';

import PropTypes from 'prop-types';

import Grid from '@ulta/core/components/GridContainer/GridContainer';
import Text from '@ulta/core/components/Text/Text';
import UltaAsset from '@ulta/core/components/UltaAsset/UltaAsset';
import { useIntersectionObserver } from '@ulta/core/hooks/useIntersectionObserver/useIntersectionObserver';
import { useDeviceInflection } from '@ulta/core/providers/InflectionProvider/InflectionProvider';
import { spreadProps } from '@ulta/core/utils/dom/dom';
import { handleIntersection } from '@ulta/core/utils/intersectionProcessor/intersectionProcessor';

import Tag from '@ulta/modules/Tag/Tag';

import constants from '@ulta/utils/constants/constants';

import Link from '../Link/Link';

/**
 * Represents a FullBleedHero component
 *
 * @method
 * @param {FullBleedHeroProps} props - React properties passed from composition
 * @returns FullBleedHero
 */
export const FullBleedHero = function( props ){
  const { inflection } = useDeviceInflection();
  const fullBleedHeroRef = useRef( null );
  const {
    root = defaultProps.root,
    rootMargin = defaultProps.rootMargin,
    threshold = defaultProps.threshold,
    backgroundColor,
    fullWidthImage,
    desktopImage,
    mobileImage,
    tag,
    eyebrow,
    headlineTag = defaultProps.headlineTag,
    headline,
    subheadline,
    linkAction
  } = props;
  let rightColumnImage = {
    ...( inflection.DESKTOP ? desktopImage : mobileImage )
  };
  let isItFullWidthMobile = fullWidthImage && inflection.MOBILE;
  useIntersectionObserver( fullBleedHeroRef, {
    root: root,
    rootMargin: rootMargin,
    threshold: threshold
  }, handleIntersection( props ) );

  return (
    <div
      className='FullBleedHero'
      ref={ fullBleedHeroRef }
    >
      <Grid
        fullBleedContentBackgroundColor={ backgroundColor }
        fullBleedContentWrapper={ fullWidthImage && inflection.DESKTOP }
        fullBleedContent={
          fullWidthImage && inflection.DESKTOP &&
            <UltaAsset
              { ...spreadProps( { ...desktopImage } ) }
              isBackground={ true }
              bgAnimation={ true }
            />
        }
      >
        <div
          className='FullBleedHero__leftColumn'
        >
          { tag &&
            <div className='FullBleedHero__Tag'>
              <Tag
                variant='default'
                style='white'
                message={ tag }
              />
            </div>
          }
          { eyebrow &&
            <div className='FullBleedHero__Eyebrow'>
              <Text
                htmlTag='span'
                textStyle='eyebrow'
              >
                { eyebrow }
              </Text>

            </div>
          }
          <div className='FullBleedHero__Headline'>
            <Text
              htmlTag={ headlineTag }
              textStyle='title-2'
            >
              { headline }
            </Text>
          </div>
          { subheadline &&
            <div className='FullBleedHero__Subheadline'>
              <Text
                htmlTag='p'
                textStyle='subtitle-1'
              >
                { subheadline }
              </Text>
            </div>
          }
          { linkAction?.label &&
            <div className='FullBleedHero__LinkAction'>
              <Link
                linkAction={ linkAction }
                linkIcon='ArrowForward'
                linkStyle='title-6'
              />
            </div>
          }
        </div>
        { !fullWidthImage &&
          <div className='FullBleedHero__rightColumn'>
            <UltaAsset
              { ...spreadProps( { ...rightColumnImage } ) }
            />
          </div>
        }
        { isItFullWidthMobile &&
          <div className='FullBleedHero__mobileImage'>
            <UltaAsset
              { ...spreadProps( { ...mobileImage } ) }
            />
          </div>
        }
      </Grid>
    </div>
  );
};

/**
 * Property type definitions
 * @typedef FullBleedHeroProps
 * @type {object}
 * @property {string} tag - Set the tag
 * @property {string} headline - Set the headline
 * @property {string} headlineTag - Sets the headlineTag text
 * @property {string} subheadline - Set the subheadline
 * @property {string} eyebrow - Set the eyebrow text
 * @property {string} mobileImage - Set the mobile image
 * @property {string} desktopImage - Set the desktop image
 * @property {string} altText - Set the altText for image
 * @property {string} backgroundColor - Set the background color
 * @property {boolean} fullWidthImage - Set the boolean flag for image type
 * @property {object} linkAction - Sets the linkAction properties
 */
export const propTypes = {
  /** Set the tag for display */
  tag: PropTypes.string,
  /** Set the headline for display */
  headline: PropTypes.string,
  /** Sets the headlineTag for display */
  headlineTag: PropTypes.string,
  /** Set the subheadline for display */
  subheadline: PropTypes.string,
  /** Set the eyebrow for display */
  eyebrow: PropTypes.string,
  /** Set the mobileImage for display */
  mobileImage: PropTypes.object,
  /** Set the desktopImage for display */
  desktopImage: PropTypes.object,
  /** Set the altText for display */
  altText: PropTypes.string,
  /** Set the backgroundColor for display */
  backgroundColor: PropTypes.string,
  /** Set the boolean for image type */
  fullWidthImage: PropTypes.bool,
  /** Sets the linkAction properties */
  linkAction: PropTypes.object
};

/**
 * Default values for passed properties
 * @type object
 * @property {string} headlineTag - Default headlineTag is h2
 */
export const defaultProps = {
  headlineTag: 'h2',
  ...constants.INTERSECTION_OBSERVER_OPTIONS
};

FullBleedHero.defaultProps = defaultProps;
FullBleedHero.propTypes = propTypes;

export default FullBleedHero;